import "./accent-theme-picker.css";
import { useEffect, useMemo, useState } from "react";
import {
    ACCENT_PRESETS,
    ACCENT_THEME_STORAGE_KEY,
    applyAccentFromHex,
    clearAccentOverrides,
    hexToRgb,
    loadAndApplyStoredAccent,
    normalizeHex,
    saveAccentCustom,
    saveAccentPreset,
} from "utils/accent-theme";

function readStoredSelection() {
    try {
        const raw = localStorage.getItem(ACCENT_THEME_STORAGE_KEY);
        if (!raw) {
            return { kind: "default" };
        }
        const data = JSON.parse(raw);
        if (data?.kind === "preset" && data.id) {
            return { kind: "preset", id: data.id };
        }
        if (data?.kind === "custom" && data.hex) {
            return { kind: "custom", hex: data.hex };
        }
    } catch {
        /* ignore */
    }
    return { kind: "default" };
}

const PaletteIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
        <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
        <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
        <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
);

const AccentThemePicker = () => {
    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState(() => ({
        kind: "default",
    }));

    useEffect(() => {
        loadAndApplyStoredAccent();
        setSelection(readStoredSelection());
    }, []);

    const customHex = useMemo(() => {
        if (selection.kind === "custom") {
            return selection.hex;
        }
        const preset = ACCENT_PRESETS.find((p) => p.id === selection.id);
        if (preset) {
            return preset.hex;
        }
        const ocean = ACCENT_PRESETS.find((p) => p.id === "ocean");
        return ocean ? ocean.hex : "#0871a1";
    }, [selection]);

    const handlePreset = (preset) => {
        applyAccentFromHex(preset.hex);
        saveAccentPreset(preset.id);
        setSelection({ kind: "preset", id: preset.id });
    };

    const handleColorInput = (e) => {
        const hex = e.target.value;
        if (hexToRgb(hex)) {
            applyAccentFromHex(hex);
            saveAccentCustom(hex);
            setSelection({ kind: "custom", hex });
        }
    };

    const handleReset = () => {
        clearAccentOverrides();
        setSelection({ kind: "default" });
        setOpen(false);
    };

    const isPresetActive = (id) =>
        selection.kind === "preset" && selection.id === id;

    return (
        <div className="ln-accent-picker">
            <button
                type="button"
                className="ln-accent-picker__toggle"
                aria-expanded={open}
                aria-controls="ln-accent-picker-panel"
                aria-label="Choose site accent color"
                onClick={() => setOpen((o) => !o)}
            >
                <PaletteIcon />
            </button>
            {open ? (
                <div
                    className="ln-accent-picker__panel"
                    id="ln-accent-picker-panel"
                    role="region"
                    aria-label="Accent color options"
                >
                    <p className="ln-accent-picker__title">Accent color</p>
                    <p className="ln-accent-picker__hint">
                        Preview the site in different brand hues. Your choice is
                        saved in this browser only.
                    </p>
                    <div className="ln-accent-picker__presets">
                        {ACCENT_PRESETS.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                className={`ln-accent-picker__chip${
                                    isPresetActive(p.id)
                                        ? " ln-accent-picker__chip--active"
                                        : ""
                                }`}
                                onClick={() => handlePreset(p)}
                            >
                                <span
                                    className="ln-accent-picker__swatch"
                                    style={{ backgroundColor: p.hex }}
                                    aria-hidden="true"
                                />
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="ln-accent-picker__custom">
                        <label htmlFor="ln-accent-custom">Custom</label>
                        <input
                            id="ln-accent-custom"
                            className="ln-accent-picker__color-input"
                            type="color"
                            value={normalizeHex(customHex) || "#0871a1"}
                            onChange={handleColorInput}
                            aria-label="Pick a custom accent color"
                        />
                    </div>
                    <button
                        type="button"
                        className="ln-accent-picker__reset"
                        onClick={handleReset}
                    >
                        Use site default
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default AccentThemePicker;
